"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  useToast,
  FormErrorMessage,
  HStack,
  Icon,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { TbCalendarUp } from "react-icons/tb";

interface BookAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => Promise<{ success: boolean; error?: string }>;
  initialValues?: any;
}

export const BookAppointmentModal: React.FC<BookAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialValues,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [calApiKey, setCalApiKey] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");
  const [timezone, setTimezone] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const [saveError, setSaveError] = useState("");

  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setDescription(initialValues.description || "");
      setCalApiKey(initialValues.cal_api_key || "");
      setEventTypeId(initialValues.event_type_id?.toString() || "");
      setTimezone(initialValues.timezone || "");
    } else {
      setName("book_appointment");
      setDescription(
        "When users ask to book an appointment, book it on the calendar."
      );
      setCalApiKey("");
      setEventTypeId("");
      setTimezone("America/Los_Angeles");
    }
    setSaveError("");
  }, [initialValues, isOpen]);

  const validateToolName = (val: string) => {
    if (!val.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (val.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(val)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = async () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    setSaveError("");

    try {
      const result = await onSave({
        type: "book_appointment_cal",
        name,
        description,
        cal_api_key: calApiKey,
        event_type_id: Number.parseInt(eventTypeId) || 0,
        timezone,
      });

      if (result.success) {
        onClose();
      } else {
        setSaveError(
          result.error || "Failed to save the function. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Error saving function:", error);
      setSaveError(
        error?.response?.data?.message ||
          error.message ||
          "An unexpected error occurred"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isEventTypeIdValid = eventTypeId === "" || /^\d+$/.test(eventTypeId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent
        bg="white"
        color="gray.800"
        borderRadius="xl"
        border="1px"
        borderColor="gray.200"
      >
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={TbCalendarUp} boxSize={5} />
            <span>Book on the Calendar (Cal.com)</span>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {saveError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">{saveError}</AlertDescription>
              </Alert>
            )}

            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel>Name</FormLabel>
              <Input
                id="book-appointment-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter function name"
                variant="outline"
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                bg="white"
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel>Description (Optional)</FormLabel>
              <Textarea
                id="book-appointment-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                variant="outline"
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                bg="white"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>API Key (Cal.com)</FormLabel>
              <Input
                id="book-appointment-function-api-key"
                value={calApiKey}
                type="password"
                onChange={(e) => setCalApiKey(e.target.value)}
                placeholder="Enter Cal.com API key"
                variant="outline"
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                bg="white"
              />
            </FormControl>

            <FormControl isRequired isInvalid={!isEventTypeIdValid}>
              <FormLabel>Event Type ID (Cal.com)</FormLabel>
              <Input
                id="book-appointment-function-event-type-id"
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
                placeholder="Enter Event Type ID"
                variant="outline"
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                bg="white"
              />
              {!isEventTypeIdValid && (
                <FormErrorMessage>
                  Event Type ID must be numeric
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Timezone (Optional)</FormLabel>
              <Input
                id="book-appointment-function-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Enter Timezone (e.g., America/Los_Angeles)"
                variant="outline"
                borderColor="gray.300"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "primary.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                }}
                bg="white"
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Saving..."
            isDisabled={!isEventTypeIdValid || !calApiKey || !!nameError}
            bg="primary.500"
            color="white"
            _hover={{ bg: "primary.600" }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
